import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { listAgents, inviteAgent, disableAgent, SUPPORT_CAPABILITIES } from "../../lib/supportWorkspace";

const STATUS_TONE = {
  invited: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  disabled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdminSupportAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("both");
  const [caps, setCaps] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAgents(await listAgents());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCap = (c) => setCaps((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await inviteAgent({ email: email.trim(), scope, capabilities: caps });
      toast.success(`Invite sent to ${email}`);
      setEmail(""); setCaps([]); setShowInvite(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const disable = async (agent) => {
    const reason = window.prompt(`Reason for disabling ${agent.invited_email}?`);
    if (!reason) return;
    try {
      await disableAgent(agent.id, reason);
      toast.success("Agent disabled");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-support-agents-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-xl font-semibold text-slate-900">Support agents</h1>
        <button onClick={() => setShowInvite((v) => !v)} data-testid="admin-support-agent-invite-toggle"
                className="btn-brand px-4 py-1.5 text-sm">
          {showInvite ? "Cancel" : "Invite agent"}
        </button>
      </div>

      {showInvite && (
        <form onSubmit={submitInvite} className="rounded-xl border border-slate-200 bg-white p-5 mb-6 space-y-3">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="agent@example.com"
                 data-testid="admin-support-agent-email-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="admin-support-agent-scope-select"
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm">
            <option value="both">Both business &amp; renter cases</option>
            <option value="business">Business cases only</option>
            <option value="renter">Renter cases only</option>
          </select>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Capabilities</p>
            <div className="flex flex-wrap gap-2">
              {SUPPORT_CAPABILITIES.map((c) => (
                <label key={c} className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer ${
                  caps.includes(c) ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-600"
                }`}>
                  <input type="checkbox" className="hidden" checked={caps.includes(c)} onChange={() => toggleCap(c)} />
                  {c}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" data-testid="admin-support-agent-invite-submit" className="btn-brand px-4 py-1.5 text-sm">
            Send invite
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="space-y-2">
          {agents.map((a) => (
            <div key={a.id} data-testid={`admin-support-agent-row-${a.id}`}
                 className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{a.invited_email}</p>
                <p className="text-xs text-slate-500">
                  {a.scope} · {(a.capabilities || []).length} capabilities
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase tracking-wide ${STATUS_TONE[a.status]}`}>
                  {a.status}
                </span>
                {a.status !== "disabled" && (
                  <button onClick={() => disable(a)} data-testid={`admin-support-agent-disable-${a.id}`}
                          className="text-xs px-2.5 py-1 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50">
                    Disable
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
